<!-- eslint-disable vue/no-v-html -->
<template>
  <div>
    <div class="table-responsive mt-2">
      <table ref="table" class="table table-striped table-hover table-sm w-100 small">
        <thead class="thead-dark">
          <tr>
            <th
              v-for="col in activeColumnList"
              :key="`${col.key}-header`"
              :class="getColumnHeaderClasses(col.key)"
              v-html="col.label"
            />
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading">
            <td :colspan="activeColumnList.length">
              Loading data...
            </td>
          </tr>
          <tr v-else-if="error">
            <td :colspan="activeColumnList.length">
              Error: {{ error.message }}
            </td>
          </tr>
          <tr v-for="(row, index) in sortedRows" :key="index">
            <td
              v-for="col in activeColumnList"
              :key="`${col.key}-row-${index}`"
              :class="getColumnCellClasses(col.key)"
            >
              <template v-if="row[col.key].htmlValue">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="row[col.key].htmlValue" />
              </template>
              <template v-else-if="col.type === 'boolean'">
                <checkmark-icon v-if="row[col.key].displayValue" />
                <span v-else class="muted">-</span>
              </template>
              <template v-else>
                {{ row[col.key].displayValue }}
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { get } from 'object-path';

const { isArray } = Array;
const { keys } = Object;

export default {
  /**
   *
   */
  props: {
    tableRows: {
      type: Array,
      default: () => ([]),
    },
    columns: {
      type: Object,
      default: () => ({}),
    },
  },

  /**
   *
   */
  data: () => ({
    isLoading: false,
    error: null,
    rows: [],
  }),

  /**
   *
   */
  computed: {
    /**
     *
     */
    columnList() {
      const { columns } = this;
      const colKeys = keys(columns);
      return colKeys.map((key) => {
        const col = columns[key];
        return { ...col, key, label: col.label || key };
      });
    },

    activeColumnList() {
      return this.columnList.filter((v) => !v.hidden);
    },

    /**
     *
     */
    filteredRows() {
      return this.rows;
    },

    /**
     *
     */
    sortedRows() {
      return this.filteredRows;
    },
  },

  /**
   *
   */
  created() {
    this.loadData();
  },

  /**
   *
   */
  methods: {
    /**
     *
     */
    getColumnHeaderClasses(key) {
      const classes = [
        'text-center',
        'align-middle',
        'table-header',
        `table-header__${key}`,
      ];
      const found = this.columnList.find((col) => col.key === key);
      if (found && !found.sortable) classes.push('table-header--static');
      return classes.join(' ');
    },

    /**
     *
     */
    getColumnCellClasses(key) {
      const classes = [
        'text-center',
        `col__${key}`,
      ];
      const { selectedSortKey } = this;
      if (selectedSortKey && selectedSortKey === key) classes.push('col--sorted');
      return classes.join(' ');
    },

    /**
     *
     * @param {*} value The number value to format
     * @param {*} currency The currency to use
     */
    formatCurrency(value, currency) {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
      });
      return formatter.format(value);
    },

    /**
     *
     */
    getColumn(key) {
      return this.columns[key];
    },

    /**
     *
     */
    getDisplayValue(col, row) {
      const { key } = col;
      const value = get(row, key);
      if (!value || !value.raw) return '';
      if (col.isCurrency) return this.formatCurrency(value.raw, row.Currency);
      return value.raw;
    },

    /**
     *
     */
    async loadData() {
      this.isLoading = true;

      let rows = this.tableRows;
      const categories = ['all'];
      const countries = [];

      // Format/simplify the raw data.
      this.rows = isArray(rows) ? rows.map((row) => {
        const newRow = this.columnList.reduce((o, col) => {
          const { key } = col;

          const raw = row[key];
          if (key === 'categories') (raw || []).forEach((v) => categories.push(v));
          if (key === 'country' && raw) countries.push(raw);
          const values = { raw };
          return { ...o, [key]: values };
        }, {});

        // calculate display value off of formatted row.
        this.columnList.forEach((col) => {
          const displayValue = this.getDisplayValue(col, newRow);
          newRow[col.key].displayValue = displayValue;
          if (col.key === 'name' && row.url) {
            newRow[col.key].htmlValue = `<a href="${row.url}">${displayValue}</a>`;
          }
        });
        return newRow;
      }) : [];
      // clear the rows after mapping to allow for gc.
      rows = [];
      this.categories = [...new Set(categories)];
      this.countries = ['all', ...[...new Set(countries)].sort()];
      this.isLoading = false;
    },
  },
};
</script>
